import React, { useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { Link } from "react-router-dom";
import PaginationComponent from "../../Components/PaginationComponent";
import toast from "react-hot-toast";
import medicineService from "../../Services/medicineService";

const MedicalStore = () => {
  const [searchData, setSearchData]     = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [paginatedData, setPaginatedData] = useState([]);
  const itemsPerPage = 10;
  const [currentPage, setCurrentPage]   = useState(1);
  const [records, setRecords]           = useState([]);
  const [loading, setLoading]           = useState(false);

  useEffect(() => {
    const getMedicines = async () => {
      try {
        setLoading(true);
        const response = await medicineService.fetchAll();
        setRecords(response.medicines);
      } catch (error) {
        toast.error("Error fetching medicines");
      } finally {
        setLoading(false);
      }
    };
    getMedicines();
  }, []);

  useEffect(() => {
    const filtered =
      records?.filter((item) =>
        item?.medicine_name?.toLowerCase().includes(searchData.toLowerCase())
      ) || [];
    setFilteredData(filtered);
    setCurrentPage(1);
  }, [searchData, records]);

  useEffect(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end   = start + itemsPerPage;
    setPaginatedData(filteredData.slice(start, end));
  }, [currentPage, filteredData]);

  return (
    <div className="p-3 sm:p-6">

      {/* ── HEADER CARD ─────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl px-4 sm:px-6 py-3 sm:py-4 mb-4 sm:mb-5 shadow-sm">

        {/* Single row: title on left, controls on right — never wraps */}
        <div className="flex items-center justify-between gap-3">

          {/* Left: title + count */}
          <div className="min-w-0 flex-shrink-0">
            <h1 className="text-[14px] sm:text-[17px] font-[500] text-gray-800 leading-tight">
              Medicine Records
            </h1>
            <p className="text-[11px] sm:text-[12px] text-gray-400 mt-0.5">
              {filteredData.length} records found
            </p>
          </div>

          {/* Right: search + buttons — always stay right, shrink gracefully */}
          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">

            {/* Search — hidden on xs, visible sm+ */}
            <div className="relative hidden sm:block">
              <SearchIcon
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
                style={{ fontSize: 15 }}
              />
              <input
                type="search"
                placeholder="Search medicine..."
                value={searchData}
                onChange={(e) => setSearchData(e.target.value)}
                className="pl-8 pr-3 py-[6px] w-[180px] lg:w-[220px] text-[12px] sm:text-[13px] bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-[#2F6DB4]"
              />
            </div>

            <Link to="SaleMedicine">
              <button className="bg-[#2F6DB4] hover:bg-[#255A8C] text-white text-[11px] sm:text-[13px] px-2.5 sm:px-4 py-[6px] sm:py-[7px] rounded-lg whitespace-nowrap transition-colors">
                + Sale Medicine
              </button>
            </Link>

            <Link to="SaleServices">
              <button className="bg-white text-gray-700 text-[11px] sm:text-[13px] px-2.5 sm:px-4 py-[6px] sm:py-[7px] rounded-lg border border-gray-300 hover:bg-gray-50 whitespace-nowrap transition-colors">
                + Sale Services
              </button>
            </Link>
          </div>
        </div>

        {/* Search — full width, only on xs (below header row) */}
        <div className="relative mt-2.5 sm:hidden">
          <SearchIcon
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
            style={{ fontSize: 15 }}
          />
          <input
            type="search"
            placeholder="Search by medicine name..."
            value={searchData}
            onChange={(e) => setSearchData(e.target.value)}
            className="pl-8 pr-3 py-[7px] w-full text-[13px] bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-[#2F6DB4]"
          />
        </div>
      </div>

      {/* ── TABLE — desktop (md+) ────────────────────────────────────── */}
      <div className="hidden md:block bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px]">
            <thead>
              <tr className="bg-[#215187] text-[11px] uppercase text-white">
                <th className="py-3 px-4 text-left">Sr No.</th>
                <th className="py-3 px-4 text-left">Medicine Name</th>
                <th className="py-3 px-4 text-left">Quantity in Stock</th>
                <th className="py-3 px-4 text-left">Price</th>
                <th className="py-3 px-4 text-left">Supplier Name</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="py-10 text-center text-[13px] text-gray-400">Loading medicines...</td>
                </tr>
              ) : paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-10 text-center text-[13px] text-gray-400">No medicines found.</td>
                </tr>
              ) : (
                paginatedData.map((medicine, index) => (
                  <tr key={medicine?.id} className="border-t hover:bg-gray-50 text-[13px]">
                    <td className="py-3 px-4 text-gray-500">
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </td>
                    <td className="py-3 px-4 font-[500] text-gray-800">{medicine?.medicine_name}</td>
                    <td className="py-3 px-4">
                      <span className={`text-[12px] px-2 py-0.5 rounded-md font-[500] ${medicine?.quantity_in_stock <= 10 ? "bg-red-50 text-red-500" : "bg-green-50 text-green-600"}`}>
                        {medicine?.quantity_in_stock}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-700">Rs. {medicine?.price}</td>
                    <td className="py-3 px-4 text-gray-700">{medicine?.supplier_name}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── CARDS — mobile (< md) ────────────────────────────────────── */}
      <div className="md:hidden space-y-2.5">
        {loading ? (
          <div className="bg-white border border-gray-200 rounded-xl py-10 text-center text-[13px] text-gray-400 shadow-sm">
            Loading medicines...
          </div>
        ) : paginatedData.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl py-10 text-center text-[13px] text-gray-400 shadow-sm">
            No medicines found.
          </div>
        ) : (
          paginatedData.map((medicine, index) => (
            <div key={medicine?.id} className="bg-white border border-gray-200 rounded-xl px-4 py-3.5 shadow-sm">
              {/* Name row + qty badge */}
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[11px] text-gray-400 font-[500] flex-shrink-0">
                    #{(currentPage - 1) * itemsPerPage + index + 1}
                  </span>
                  <span className="text-[13px] font-[600] text-gray-800 truncate">
                    {medicine?.medicine_name}
                  </span>
                </div>
                <span className={`text-[11px] px-2 py-0.5 rounded-md font-[500] flex-shrink-0 ${medicine?.quantity_in_stock <= 10 ? "bg-red-50 text-red-500" : "bg-green-50 text-green-600"}`}>
                  Qty: {medicine?.quantity_in_stock}
                </span>
              </div>
              {/* Price + supplier */}
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <div className="flex items-center gap-1">
                  <span className="text-[11px] text-gray-400">Price:</span>
                  <span className="text-[12px] font-[500] text-gray-700">Rs. {medicine?.price}</span>
                </div>
                <div className="flex items-center gap-1 min-w-0">
                  <span className="text-[11px] text-gray-400 flex-shrink-0">Supplier:</span>
                  <span className="text-[12px] text-gray-700 truncate">{medicine?.supplier_name}</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <PaginationComponent
        filteredData={filteredData}
        setPaginatedData={setPaginatedData}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
      />
    </div>
  );
};

export default MedicalStore;